import {
  INTERNAL_SYMBOL,
  LeverageParameters,
  RestRequestOrder,
  RestRequestOrderTypeStopLossMarket,
  RestRequestOrderTypeTakeProfitMarket,
} from '@katanaperps/katana-perps-sdk';
import { useMemo } from 'react';

import { getChainPrivateApi } from 'api/apiCoreSdk';
import { cancelConditionalTPSLOrder, createMarketOrder } from 'api/apiExchange';
import {
  BaseCreateOrderType,
  getCreateMarketOrderParams,
} from 'api/apiExchangePlaceOrder';
import { calculateUnrealizedPnLPercent, isPositionShort } from 'api/apiUtils';
import { useOrdersQuery } from 'api/queries/orderQueries';
import { ApiOrder, ApiTriggerType } from 'api/types';
import { isTPSLOrder } from 'components/orders/orderUtil';
import { EXCHANGE_DECIMALS } from 'config/constants';
import { BigNumberClass, BigNumberJs } from 'lib/BigNumber';
import { formatNumber } from 'lib/display/formatting';
import { signTypedDataWithModal } from 'lib/ethereum/sign/signHashWithModal';
import { TypedDataSignParams } from 'lib/ethereum/sign/typedData/eip712';
import { DelegatedKeyAuthorization } from 'reduxState/delegatedKey/authorizationReducer';
import { PerMarketStringMap } from 'reduxState/markets/listed/listedMarketsTypes';
import { useSelector } from 'reduxState/storeCore';
import { IWallet } from 'reduxState/wallet/walletReducer';
import { Wallet } from 'reduxState/wallet/walletUtils';

export function findTpSlOrders({
  market,
  allOrders,
  maximumPositionSizes,
}: {
  market: string;
  allOrders: ApiOrder[] | undefined;
  maximumPositionSizes: PerMarketStringMap;
}) {
  const maximumPositionSize = maximumPositionSizes[market];

  const takeProfitOrder = allOrders?.find(
    order =>
      order.market === market &&
      order.type === 'takeProfitMarket' &&
      maximumPositionSize &&
      isTPSLOrder(order, maximumPositionSize) &&
      order.reduceOnly
  );
  const stopLossOrder = allOrders?.find(
    order =>
      order.market === market &&
      order.type === 'stopLossMarket' &&
      maximumPositionSize &&
      isTPSLOrder(order, maximumPositionSize) &&
      order.reduceOnly
  );
  return {
    takeProfitOrder,
    stopLossOrder,
  };
}

export function useCurrentTpSlOrders(
  market: string | undefined,
  /** Get Conditional TPSL for specific order id */
  conditionalOrderId: string | undefined
) {
  const { data: allOrders } = useOrdersQuery();
  const maximumPositionSizes = useSelector(
    state => state.markets.listedMarkets.maximumPositionSizes
  );
  return useMemo(() => {
    if (!market) {
      return {
        takeProfitOrder: undefined,
        stopLossOrder: undefined,
      };
    }
    if (conditionalOrderId) {
      const parentOrder = allOrders?.find(
        order => order.orderId === conditionalOrderId
      );
      return {
        takeProfitOrder: parentOrder?.conditionalTakeProfitOrder,
        stopLossOrder: parentOrder?.conditionalStopLossOrder,
      };
    }
    return findTpSlOrders({
      allOrders,
      market,
      maximumPositionSizes,
    });
  }, [allOrders, conditionalOrderId, market, maximumPositionSizes]);
}

const prepareTpSlOrderParams = (
  {
    baseSymbol,
    quoteSymbol,
    wallet,
    delegatedWallet,
    side,
    maximumPositionSize,
  }: {
    baseSymbol: string;
    quoteSymbol: string;
    wallet: Wallet;
    side: 'buy' | 'sell';
    delegatedWallet: DelegatedKeyAuthorization | undefined;
    maximumPositionSize: string;
  },
  {
    orderType,
    price,
    triggerType,
  }: {
    orderType: 'stopLossMarket' | 'takeProfitMarket';
    price: number;
    triggerType: ApiTriggerType;
  }
) => {
  const baseOrderParams = {
    baseSymbol,
    quoteSymbol,
    delegatedKeyWallet: delegatedWallet?.walletAddress || undefined,
    quantity: maximumPositionSize,
    reduceOnly: true,
    side,
    signTypedData: (...typedData: TypedDataSignParams) =>
      signTypedDataWithModal(
        wallet,
        delegatedWallet,
        typedData,
        'Please sign the order on your device.'
      ),
    walletAddress: wallet.walletAddress,
  } satisfies BaseCreateOrderType;

  const params = {
    type: orderType,
    ...baseOrderParams,
    triggerPrice: formatNumber(price, {
      fixedDecimals: EXCHANGE_DECIMALS,
      thousandsSeparator: false,
    }),
    triggerType,
  } as const;
  return params;
};

export const prepareExecutionOfTpSlOrders =
  (params1: {
    baseSymbol: string;
    quoteSymbol: string;
    wallet: Wallet;
    side: 'buy' | 'sell';
    delegatedWallet: DelegatedKeyAuthorization;
    maximumPositionSize: string;
  }) =>
  (params2: {
    orderType: 'stopLossMarket' | 'takeProfitMarket';
    price: number;
    triggerType: ApiTriggerType;
  }) => {
    const finalParams = prepareTpSlOrderParams(params1, params2);
    return createMarketOrder(finalParams, undefined);
  };

const prepareParamsOfConditionalTpSlOrders =
  (params1: {
    baseSymbol: string;
    quoteSymbol: string;
    wallet: Wallet;
    side: 'buy' | 'sell';
    delegatedWallet: DelegatedKeyAuthorization | undefined;
    maximumPositionSize: string;
  }) =>
  async <T extends 'stopLossMarket' | 'takeProfitMarket'>(params2: {
    orderType: T;
    price: number;
    triggerType: ApiTriggerType;
  }): Promise<RestRequestOrder & { type: T }> => {
    const finalParams = prepareTpSlOrderParams(params1, params2);
    const order = getCreateMarketOrderParams(finalParams);
    if (order.type !== params2.orderType) {
      throw new Error('Order type error');
    }
    return order as RestRequestOrder & { type: T };
  };

export async function prepareBothConditionalTPSLOrders({
  market,
  maximumPositionSize,
  parentOrderSide,
  wallet,
  delegatedWallet,
  takeProfitPrice,
  takeProfitPriceType,
  stopLossPrice,
  stopLossPriceType,
}: {
  market: string;
  maximumPositionSize: string;
  parentOrderSide: 'buy' | 'sell';
  wallet: Wallet;
  delegatedWallet: DelegatedKeyAuthorization | undefined;
  takeProfitPrice: string | number | undefined;
  takeProfitPriceType: 'index' | 'last' | undefined;
  stopLossPrice: string | number | undefined;
  stopLossPriceType: 'index' | 'last' | undefined;
}) {
  const [base, quote] = market.split('-');
  const getTPSLOrder = prepareParamsOfConditionalTpSlOrders({
    baseSymbol: base,
    quoteSymbol: quote,
    delegatedWallet: delegatedWallet,
    maximumPositionSize,
    side: parentOrderSide === 'buy' ? 'sell' : 'buy',
    wallet,
  });
  let takeProfit: (RestRequestOrder & { type: 'takeProfitMarket' }) | undefined;
  let stopLoss: (RestRequestOrder & { type: 'stopLossMarket' }) | undefined;

  if (takeProfitPrice && takeProfitPriceType) {
    takeProfit = await getTPSLOrder({
      orderType: 'takeProfitMarket',
      price: Number(takeProfitPrice),
      triggerType: takeProfitPriceType,
    });
  }
  if (stopLossPrice && stopLossPriceType) {
    stopLoss = await getTPSLOrder({
      orderType: 'stopLossMarket',
      price: Number(stopLossPrice),
      triggerType: stopLossPriceType,
    });
  }
  return { takeProfit, stopLoss };
}

/**
 * Calculate rough estimate of expected TPSL.
 * Note that it does not account for price change while executing, so most likely will be better than real price.
 * Spec https://docs.google.com/document/d/1WXbIxalYsrbwciwM8b9tZv2VBl_9H7XBSIVpSrunZp4/edit#heading=h.s7u3x6k12ros
 */
export function estimateTPSLProfitOrLoss({
  price,
  entryPrice,
  quantity,
  marketLeverageParams,
  initialMarginFractionOverride,
}: {
  price: string;
  entryPrice: string;
  quantity: string;
  marketLeverageParams: LeverageParameters;
  initialMarginFractionOverride: string | null;
}): {
  pnL: number;
  pnLPercentage?: number;
} {
  const isShort = isPositionShort({ quantity });
  const quantityNum = Math.abs(Number(quantity));
  const priceNum = Number(price);
  const entryPriceNum = Number(entryPrice);

  if (!quantityNum || !priceNum) {
    return { pnL: 0, pnLPercentage: 0 };
  }

  const entryValue = entryPriceNum * quantityNum;
  const finalValue = priceNum * quantityNum;
  const valueDiff = entryValue - finalValue;

  const pnL = (isShort ? 1 : -1) * valueDiff;
  // const pnLPercentage = (pnL / Math.abs(entryValue)) * 100;
  const pnLPercentage = calculateUnrealizedPnLPercent(
    { entryPrice, quantity, unrealizedPnL: pnL },
    marketLeverageParams,
    initialMarginFractionOverride
  );
  return {
    pnL,
    pnLPercentage,
  };
}

export const replaceConditionalTpSl = ({
  wallet,
  delegatedWallet,
  parentOrderId,
  conditionalOrder,
}: {
  wallet: IWallet;
  delegatedWallet: DelegatedKeyAuthorization;
  parentOrderId: string;
  conditionalOrder:
    | RestRequestOrderTypeStopLossMarket
    | RestRequestOrderTypeTakeProfitMarket;
}) => {
  return getChainPrivateApi()[INTERNAL_SYMBOL].placeConditionalTpSlOrder(
    conditionalOrder,
    parentOrderId,
    (...typedData: TypedDataSignParams) =>
      signTypedDataWithModal(
        wallet,
        delegatedWallet,
        typedData,
        'Please sign TPSL order on your device.'
      )
  );
};

export const cancelConditionalTPSLOrderWithSignModal = (
  parentOrderId: string,
  type: 'takeProfit' | 'stopLoss',
  wallet: IWallet,
  delegatedWallet?: DelegatedKeyAuthorization
): Promise<unknown> => {
  return cancelConditionalTPSLOrder({
    walletAddress: wallet.walletAddress,
    parentOrderId,
    delegatedKey: delegatedWallet,
    type,
    signTypedDataFn: (...typedData) =>
      signTypedDataWithModal(
        wallet,
        delegatedWallet,
        typedData,
        'Please sign the transaction to cancel your order.'
      ),
  });
};

/** 1 or -1 */
function calculateTPSLMultiplier({
  targetAction,
  trigger,
}: {
  targetAction: 'buy' | 'sell';
  trigger: 'stopLoss' | 'takeProfit';
}) {
  return targetAction === 'buy'
    ? trigger === 'stopLoss'
      ? 1
      : -1
    : trigger === 'stopLoss'
    ? -1
    : 1;
}

/**
 * Current vs target price
 *  eg. sell takeProfit from current 100 to 150 (target) is 50%
 *      sell stopLoss from current 100 to 50 is also 50%
 * See unit tests for more examples.
 */
export function calculateTPSLPriceChangePercent({
  targetPrice,
  targetAction,
  entryPrice,
  trigger,
  triggerBy,
  initialMarginFraction,
}: {
  targetPrice: number | string | undefined;
  targetAction: 'buy' | 'sell';
  entryPrice: number | string;
  trigger: 'stopLoss' | 'takeProfit';
  triggerBy: 'byChange' | 'byROI';
  /** Only required for triggerBy=byROI */
  initialMarginFraction: number | string | undefined;
}) {
  if (!targetPrice) {
    return undefined;
  }
  if (!Number(targetPrice)) {
    return 0;
  }
  const multiplier = calculateTPSLMultiplier({ targetAction, trigger });
  const diff = BigNumberJs(targetPrice).minus(entryPrice);
  const priceChange = diff.multipliedBy(multiplier);

  if (triggerBy === 'byChange') {
    // TODO: need convert to Number ?
    return priceChange.dividedBy(entryPrice).multipliedBy(100).toNumber();
  }
  // Trigger by ROI

  // TODO: possible to merge with "Estimated P&L" calculation ?
  // ROI % = (TP Price - Entry Price)	/	(Entry Price * IMF)   --IMF for market and wallet
  if (!initialMarginFraction) {
    return 0;
  }
  const entryPriceBN = BigNumberJs(entryPrice);
  const targetPriceBN = BigNumberJs(targetPrice);
  const imfBN = BigNumberJs(initialMarginFraction);
  if (imfBN.isZero()) {
    return 0;
  }
  const numerator = targetPriceBN.minus(entryPriceBN).multipliedBy(multiplier);
  const denominator = entryPriceBN.multipliedBy(imfBN);
  const roiBN = numerator.dividedBy(denominator).multipliedBy(100);
  return roiBN.toNumber();
}

export function calculateTPSLTargetPriceFromPercentage({
  targetPercentage,
  targetAction,
  entryPrice,
  trigger,
  triggerBy,
  initialMarginFraction,
}: {
  targetPercentage: number | string;
  targetAction: 'buy' | 'sell';
  entryPrice: number | string;
  trigger: 'stopLoss' | 'takeProfit';
  triggerBy: 'byChange' | 'byROI';
  /** Only required for triggerBy=byROI */
  initialMarginFraction: number | string | undefined;
}): BigNumberClass | undefined {
  const multiplier = calculateTPSLMultiplier({
    targetAction,
    trigger,
  });
  const percentageBN = BigNumberJs(targetPercentage);
  if (triggerBy === 'byChange') {
    if (trigger === 'stopLoss' && percentageBN.isGreaterThanOrEqualTo(100)) {
      // Loss is limited to full 100% of quantity
      return BigNumberJs(0);
    }
    const targetPrice = BigNumberJs(entryPrice)
      .times(BigNumberJs(multiplier).plus(percentageBN.div(100)))
      .abs();
    return targetPrice;
  }

  // Trigger by ROI
  if (!initialMarginFraction) {
    return BigNumberJs(0);
  }
  const entryPriceBN = BigNumberJs(entryPrice);
  const imfBN = BigNumberJs(initialMarginFraction);
  if (imfBN.isZero()) {
    return undefined;
  }
  const roiBN = percentageBN.dividedBy(100);
  const delta = roiBN
    .multipliedBy(entryPriceBN)
    .multipliedBy(imfBN)
    .dividedBy(multiplier);
  const targetPriceBN = entryPriceBN.plus(delta);
  if (targetPriceBN.lte(0)) {
    return BigNumberJs(0);
  }
  return targetPriceBN;
}
